// Telegram WebApp SDK integration

declare global {
  interface Window {
    Telegram?: {
      WebApp?: TelegramWebApp
    }
  }
}

interface TelegramWebApp {
  initData: string
  initDataUnsafe: {
    user?: {
      id: number
      first_name: string
      last_name?: string
      username?: string
      photo_url?: string
      language_code?: string
    }
    start_param?: string
  }
  /** Client id: ios | android | android_x | tdesktop | macos | web | weba | webk | … */
  platform?: string
  colorScheme: "light" | "dark"
  themeParams: Record<string, string>
  isExpanded: boolean
  /** Bot API 8.0+ — true when immersive fullscreen is active */
  isFullscreen?: boolean
  viewportHeight: number
  viewportStableHeight: number

  // Newer Telegram Mini Apps API fields (may be missing depending on client)
  safeAreaInset?: {
    top: number
    bottom: number
    left: number
    right: number
  }
  contentSafeAreaInset?: {
    top: number
    bottom: number
    left: number
    right: number
  }

  // Event API
  onEvent?: (
    eventType: string,
    eventHandler: (payload?: unknown) => void
  ) => void
  offEvent?: (
    eventType: string,
    eventHandler: (payload?: unknown) => void
  ) => void

  ready: () => void
  expand: () => void
  /** Open a URL in the external browser (or in-app browser on supported clients). */
  openLink: (url: string, options?: { try_instant_view?: boolean }) => void
  /** Open a t.me link inside Telegram; Mini App is NOT closed (Bot API 6.1+). */
  openTelegramLink: (url: string) => void
  /** Bot API 8.0+ — immersive fullscreen (not the same as expand()) */
  requestFullscreen?: () => void
  exitFullscreen?: () => void
  close: () => void
  enableClosingConfirmation: () => void
  disableClosingConfirmation: () => void
  setHeaderColor: (color: string) => void
  setBackgroundColor: (color: string) => void
  /** Bot API 7.7+ — disable swipe-down minimize/close gestures (unsupported clients omit). */
  disableVerticalSwipes?: () => void
  enableVerticalSwipes?: () => void
  BackButton: {
    isVisible: boolean
    show: () => void
    hide: () => void
    onClick: (callback: () => void) => void
    offClick: (callback: () => void) => void
  }
  MainButton: {
    text: string
    color: string
    textColor: string
    isVisible: boolean
    isActive: boolean
    isProgressVisible: boolean
    setText: (text: string) => void
    show: () => void
    hide: () => void
    enable: () => void
    disable: () => void
    showProgress: (leaveActive?: boolean) => void
    hideProgress: () => void
    onClick: (callback: () => void) => void
    offClick: (callback: () => void) => void
  }
  HapticFeedback: {
    impactOccurred: (
      style: "light" | "medium" | "heavy" | "rigid" | "soft"
    ) => void
    notificationOccurred: (type: "error" | "success" | "warning") => void
    selectionChanged: () => void
  }
}

/** expand / requestFullscreen are for mobile bottom-sheet → full height; desktop/web use a fixed panel */
const TELEGRAM_MOBILE_PLATFORMS = new Set(["ios", "android", "android_x"])

function isTelegramMobileClient(webApp: TelegramWebApp): boolean {
  const platform = webApp.platform?.trim().toLowerCase()
  return !!platform && TELEGRAM_MOBILE_PLATFORMS.has(platform)
}

class TelegramService {
  private webApp: TelegramWebApp | null = null
  // Track max-seen bottom inset so keyboard open/close can't zero it out
  private maxSeenSafeBottom = 0
  /** Device top inset (notch); fullscreen can transiently zero SDK/CSS — keep last good value */
  private maxSeenPhysicalTop = 0

  /**
   * Read `env(safe-area-inset-top)` as px — common WebKit pattern when you need a number in JS.
   * Not a Telegram API; merged with SDK/CSS via Math.max so a stray 0 does not shrink the layout alone.
   */
  private readEnvSafeAreaInsetTopPx(): number {
    if (typeof document === "undefined") return 0
    const el = document.createElement("div")
    el.setAttribute("aria-hidden", "true")
    el.style.cssText =
      "position:fixed;left:-100px;top:0;width:1px;height:0;padding-top:env(safe-area-inset-top);visibility:hidden;pointer-events:none"
    try {
      document.documentElement.appendChild(el)
      const pt = parseFloat(getComputedStyle(el).paddingTop) || 0
      return Number.isFinite(pt) ? pt : 0
    } finally {
      el.remove()
    }
  }

  private readonly applySafeAreaInsets = () => {
    if (typeof document === "undefined" || !this.webApp) return

    // Mark environment (useful for CSS targeting if needed)
    document.documentElement.classList.add("tg-miniapp")

    const parsePx = (v: string) => {
      const n = parseFloat(String(v).replace("px", "").trim())
      return Number.isFinite(n) ? n : 0
    }

    const css = getComputedStyle(document.documentElement)

    // Try CSS variables injected by Telegram
    const cssContentTop = parsePx(
      css.getPropertyValue("--tg-content-safe-area-inset-top")
    )
    const cssSafeTop = parsePx(css.getPropertyValue("--tg-safe-area-inset-top"))
    const cssContentBottom = parsePx(
      css.getPropertyValue("--tg-content-safe-area-inset-bottom")
    )
    const cssSafeBottom = parsePx(
      css.getPropertyValue("--tg-safe-area-inset-bottom")
    )

    // Telegram SDK API values (newer clients)
    const sdkContentTop = this.webApp.contentSafeAreaInset?.top ?? 0
    const sdkSafeTop = this.webApp.safeAreaInset?.top ?? 0
    const sdkContentBottom = this.webApp.contentSafeAreaInset?.bottom ?? 0
    const sdkSafeBottom = this.webApp.safeAreaInset?.bottom ?? 0

    // Content safe area = area under Telegram header controls (Close button, etc.)
    // Safe area = physical device notch/rounded corners
    // We need BOTH combined for proper spacing
    const contentTop = Math.max(sdkContentTop, cssContentTop)
    const isFullscreen = !!this.webApp.isFullscreen
    const envTop = isFullscreen ? this.readEnvSafeAreaInsetTopPx() : 0
    const rawPhysicalTop = Math.max(sdkSafeTop, cssSafeTop, envTop)
    this.maxSeenPhysicalTop = Math.max(this.maxSeenPhysicalTop, rawPhysicalTop)
    const safeTop =
      isFullscreen && rawPhysicalTop < 1 && this.maxSeenPhysicalTop >= 1
        ? this.maxSeenPhysicalTop
        : rawPhysicalTop
    const contentBottom = Math.max(sdkContentBottom, cssContentBottom)
    const safeBottom = Math.max(sdkSafeBottom, cssSafeBottom)

    // Total top inset = device safe area + content safe area (Telegram controls)
    const effectiveTop = safeTop + contentTop
    const rawBottom = Math.max(safeBottom, contentBottom)
    // Never allow bottom to drop below the max value we've seen — protects against
    // transient 0 values emitted during keyboard open/close animations.
    this.maxSeenSafeBottom = Math.max(this.maxSeenSafeBottom, rawBottom)
    const effectiveBottom = this.maxSeenSafeBottom

    // Set CSS variables
    document.documentElement.style.setProperty(
      "--app-safe-top",
      `${effectiveTop}px`
    )
    document.documentElement.style.setProperty(
      "--app-safe-bottom",
      `${effectiveBottom}px`
    )
    document.documentElement.style.setProperty("--app-tg-top-guard", "0px")

    if (import.meta.env.DEV) {
      console.log("[Telegram] Safe area insets applied:", {
        isFullscreen,
        sdk: {
          contentTop: sdkContentTop,
          safeTop: sdkSafeTop,
          contentBottom: sdkContentBottom,
          safeBottom: sdkSafeBottom,
        },
        css: {
          contentTop: cssContentTop,
          safeTop: cssSafeTop,
          contentBottom: cssContentBottom,
          safeBottom: cssSafeBottom,
        },
        envTop,
        rawPhysicalTop,
        maxSeenPhysicalTop: this.maxSeenPhysicalTop,
        effective: { top: effectiveTop, bottom: effectiveBottom },
      })
    }
  }

  constructor() {
    if (typeof window === "undefined") return

    // In some clients the WebApp object becomes available slightly позже.
    // Retry a few times to avoid a race on initial load.
    const tryAttach = () => {
      if (!this.webApp && window.Telegram?.WebApp) {
        this.webApp = window.Telegram.WebApp
        this.init()
        return true
      }
      return !!this.webApp
    }

    // Try immediately
    if (tryAttach()) return

    // Try again on window load
    window.addEventListener("load", tryAttach, { once: true })

    // Short retry loop (max ~3s)
    let attempts = 0
    const t = window.setInterval(() => {
      attempts += 1
      if (tryAttach() || attempts >= 15) window.clearInterval(t)
    }, 200)
  }

  private init() {
    if (!this.webApp) return

    this.webApp.ready()
    const mobileClient = isTelegramMobileClient(this.webApp)

    if (mobileClient) {
      this.webApp.expand()
    } else if (import.meta.env.DEV) {
      console.log(
        "[Telegram] skip expand/fullscreen on non-mobile platform:",
        this.webApp.platform ?? "(unknown)"
      )
    }

    this.webApp.setHeaderColor("#0f1419")
    this.webApp.setBackgroundColor("#0f1419")
    this.webApp.enableClosingConfirmation()

    try {
      if (typeof this.webApp.disableVerticalSwipes === "function") {
        this.webApp.disableVerticalSwipes()
      }
    } catch (e) {
      if (import.meta.env.DEV) {
        console.warn("[Telegram] disableVerticalSwipes skipped", e)
      }
    }

    if (mobileClient) {
      // Some WebApp builds expose requestFullscreen but throw WebAppMethodUnsupported
      // (e.g. protocol 6.0) — a typeof check is not enough.
      try {
        if (typeof this.webApp.requestFullscreen === "function") {
          this.webApp.requestFullscreen()
        }
      } catch (e) {
        if (import.meta.env.DEV) {
          console.warn("[Telegram] requestFullscreen skipped", e)
        }
      }
    }

    // Telegram fullscreen safe-area: apply once and keep synced.
    // viewportChanged fires with isStateStable=false while keyboard is animating —
    // skip those frames to prevent safe-area values from temporarily going to 0
    // and "sticking" there after the keyboard closes.
    this.applySafeAreaInsets()
    this.webApp.onEvent?.(
      "viewportChanged",
      (e?: { isStateStable?: boolean }) => {
        if (e?.isStateStable === false) return
        this.applySafeAreaInsets()
      }
    )
    const refreshInsetsAfterFullscreen = () => {
      this.applySafeAreaInsets()
      requestAnimationFrame(() => {
        this.applySafeAreaInsets()
        requestAnimationFrame(() => this.applySafeAreaInsets())
      })
    }

    this.webApp.onEvent?.("fullscreenChanged", refreshInsetsAfterFullscreen)
    this.webApp.onEvent?.("safeAreaChanged", () => this.applySafeAreaInsets())
    this.webApp.onEvent?.("contentSafeAreaChanged", () =>
      this.applySafeAreaInsets()
    )
    this.webApp.onEvent?.("fullscreenFailed", (e?: { error?: string }) => {
      if (import.meta.env.DEV) {
        console.warn("[Telegram] fullscreenFailed", e?.error ?? e)
      }
      if (
        this.webApp &&
        isTelegramMobileClient(this.webApp) &&
        !this.webApp.isExpanded
      ) {
        this.webApp.expand()
      }
    })
  }

  get initData(): string {
    // Prefer live value from window to avoid race with async init
    return window.Telegram?.WebApp?.initData || this.webApp?.initData || ""
  }

  get user() {
    return this.webApp?.initDataUnsafe?.user || null
  }

  get isAvailable(): boolean {
    return !!this.webApp
  }

  get colorScheme(): "light" | "dark" {
    return this.webApp?.colorScheme || "dark"
  }

  hapticImpact(
    style: "light" | "medium" | "heavy" | "rigid" | "soft" = "medium"
  ) {
    this.webApp?.HapticFeedback?.impactOccurred(style)
  }

  hapticNotification(type: "error" | "success" | "warning") {
    this.webApp?.HapticFeedback?.notificationOccurred(type)
  }

  hapticSelection() {
    this.webApp?.HapticFeedback?.selectionChanged()
  }

  openLink(url: string, options?: { try_instant_view?: boolean }) {
    this.webApp?.openLink(url, options)
  }

  openTelegramLink(url: string) {
    this.webApp?.openTelegramLink(url)
  }

  /**
   * Bot API 7.7+ — allows swipe-down minimize/close (undo `disableVerticalSwipes` from init).
   */
  enableVerticalSwipes() {
    try {
      if (
        this.webApp &&
        typeof this.webApp.enableVerticalSwipes === "function"
      ) {
        this.webApp.enableVerticalSwipes()
      }
    } catch (e) {
      if (import.meta.env.DEV) {
        console.warn("[Telegram] enableVerticalSwipes skipped", e)
      }
    }
  }

  showBackButton(callback: () => void) {
    if (this.webApp?.BackButton) {
      this.webApp.BackButton.onClick(callback)
      this.webApp.BackButton.show()
    }
  }

  hideBackButton(callback?: () => void) {
    if (!this.webApp?.BackButton) return
    if (callback) {
      this.webApp.BackButton.offClick(callback)
    }
    this.webApp.BackButton.hide()
  }
}

export const telegram = new TelegramService()
